/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { DataState, DataStateError } from '@/shared/ui';

interface AgendaStatesProps {
  isLoading: boolean;
  isError: boolean;
  /**
   * Whether the loaded agenda has no events. Accepted for API symmetry with
   * other state-gated surfaces, but the agenda empty state intentionally shows
   * only the empty calendar grid — no banner or supplementary copy.
   */
  isEmpty: boolean;
  onRetry: () => void;
  children: React.ReactNode;
}

const SKELETON_COLUMNS = Array.from({ length: 7 });
const SKELETON_ROWS = Array.from({ length: 6 });

/**
 * Renders the agenda loading skeleton fallback shown while events load.
 *
 * @returns The skeleton grid element tagged for tests.
 */
const AgendaSkeleton: React.FC = () => (
  <div
    data-testid="agenda-skeleton"
    className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)]"
  >
    <div className="grid grid-cols-7 gap-px border-b border-[var(--border-subtle)] bg-[var(--border-ultra-subtle)]">
      {SKELETON_COLUMNS.map((_, i) => (
        <div key={i} className="flex justify-center bg-[var(--surface-primary)] py-(--space-2)">
          <div className="h-3 w-8 animate-pulse rounded bg-[var(--surface-tertiary)]" />
        </div>
      ))}
    </div>
    <div className="grid flex-1 grid-cols-7 gap-px bg-[var(--border-ultra-subtle)]">
      {SKELETON_ROWS.map((_, r) =>
        SKELETON_COLUMNS.map((_, c) => (
          <div
            key={`${r}-${c}`}
            className="flex flex-col gap-(--space-1) bg-[var(--surface-primary)] p-(--space-1-5)"
          >
            {(r + c) % 3 === 0 && (
              <div className="h-2.5 w-full animate-pulse rounded-(--radius-sm) bg-[var(--surface-tertiary)]" />
            )}
            {(r + c) % 4 === 0 && (
              <div className="h-2.5 w-2/3 animate-pulse rounded-(--radius-sm) bg-[var(--surface-tertiary)]" />
            )}
          </div>
        ))
      )}
    </div>
  </div>
);

/**
 * Renders the agenda error fallback with a retry control.
 *
 * @param props The retry handler invoked when the user retries.
 * @returns The error element tagged for tests.
 */
const AgendaError: React.FC<{ onRetry: () => void }> = ({ onRetry }) => {
  const { t } = useTranslation('features/agenda');
  return (
    <div className="flex min-h-0 flex-1 flex-col rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)]">
      <DataStateError
        message={t('states.error')}
        retryLabel={t('states.retry')}
        onRetry={onRetry}
        data-testid="agenda-error"
      />
    </div>
  );
};

/**
 * Gates the agenda view through the shared DataState. While loading, only the
 * skeleton renders; on error the retry fallback shows. When loaded — including
 * when there are no events — only the (empty) calendar grid renders, by design.
 *
 * @param props State flags, retry handler and the view children.
 * @returns Rendered state wrapper.
 */
export const AgendaStates: React.FC<AgendaStatesProps> = ({
  isLoading,
  isError,
  isEmpty: _isEmpty,
  onRetry,
  children,
}) => {
  return (
    <DataState
      isLoading={isLoading}
      isError={isError}
      isEmpty={false}
      onRetry={onRetry}
      loadingFallback={<AgendaSkeleton />}
      errorState={<AgendaError onRetry={onRetry} />}
    >
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </DataState>
  );
};
